import { Promotion } from '@/@type/promotion';
import { PromotionType } from '@/__generated__/types';
import { useSnackbarHelper } from '@/components/useSnackbarHelper';
import { isValidIndex } from '@/utils/checkVariables';
import { Box, Button, Grid, Paper } from '@mui/material';
import { FormikProps } from 'formik';
import { useTranslation } from 'react-i18next';
import { CommonInput } from '../formik/commonInput';
import { Label } from '../styles';
import { createPromoValidator, initLoyaltyBenefit } from '../tools';
import { PromoSelector } from './PromoSelector';

interface promoLoyaltyBenefitsProps {
    formik: FormikProps<Promotion>;
}

export const PromoLoyaltyBenefits: React.FC<promoLoyaltyBenefitsProps> = (props: promoLoyaltyBenefitsProps) => {
    const { formik } = props;
    const { values } = formik;
    const { loyaltySettings } = values;
    const { loyaltyBenefits } = loyaltySettings;
    const { t } = useTranslation();
    const { showSnackbar } = useSnackbarHelper();
    const handleAddBenefitOnClick = (): void => {
        const newBenefit = initLoyaltyBenefit();
        const newBenefits = [...loyaltyBenefits, newBenefit];
        formik.setFieldValue('loyaltySettings.loyaltyBenefits', newBenefits);
    };

    const getLoyaltyPath = (index: number, key: string): string => {
        if (!isValidIndex(index, loyaltyBenefits)) {
            return '';
        }
        return `loyaltySettings.loyaltyBenefits[${index}].${key}`;
    };
    const handleRemoveBtnOnClick = (index: number) => {
        return (): void => {
            if (loyaltyBenefits.length === 1) {
                formik.setFieldValue('loyaltySettings.loyaltyBenefits', [initLoyaltyBenefit()]);
                return;
            }
            if (!isValidIndex(index, loyaltyBenefits)) {
                showSnackbar('Invalid index', 'error');
                return;
            }
            const newBenefits = [...loyaltyBenefits];
            newBenefits.splice(index, 1);
            formik.setFieldValue('loyaltySettings.loyaltyBenefits', newBenefits);
        };
    };

    return (
        <>
            {loyaltyBenefits.map((benefit, index) => {
                return (
                    <Paper
                        elevation={0}
                        key={index}
                        sx={{
                            padding: '16px',
                            position: 'relative',
                            border: '1px solid',
                            borderColor: 'divider'
                        }}
                    >
                        <Grid container spacing={1}>
                            <Grid size={{ xs: 12, sm: 12 }}>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'flex-end',
                                        alignItems: 'flex-end',
                                        width: '100%',
                                        minHeight: '40px'
                                    }}
                                >
                                    <Box
                                        onClick={handleRemoveBtnOnClick(index)}
                                        role="button"
                                        aria-label="Remove reward"
                                        tabIndex={0}
                                        sx={{
                                            cursor: 'pointer',
                                            color: 'primary.main',
                                            fontSize: '36px',
                                            fontWeight: 'bold',
                                            lineHeight: 1,
                                            px: 1,
                                            pb: 0,
                                            display: 'flex',
                                            alignItems: 'flex-end',
                                            '&:hover': {
                                                color: 'primary.dark'
                                            }
                                        }}
                                    >
                                        ×
                                    </Box>
                                </Box>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 12 }}>
                                <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                                    <Label>{t('Reward/s')}</Label>
                                    <PromoSelector
                                        formik={formik}
                                        formikPath={getLoyaltyPath(index, 'benefitPromotionIds')}
                                        promoValidator={createPromoValidator([PromotionType.LuckyDraw, PromotionType.Loyalty])}
                                    />
                                </Box>
                            </Grid>
                            <Grid size={{ xs: 6, sm: 6 }}>
                                <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                                    <CommonInput
                                        formik={formik}
                                        fieldPath={`loyaltySettings.loyaltyBenefits[${index}].requiredPoints`}
                                        label="Redemption Points"
                                        type="number"
                                        placeholder="Need 100 points to redeem."
                                    />
                                </Box>
                            </Grid>
                            <Grid size={{ xs: 6, sm: 6 }}>
                                <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                                    <CommonInput
                                        formik={formik}
                                        fieldPath={`loyaltySettings.loyaltyBenefits[${index}].loyaltyDurationInDays`}
                                        label="Voucher Validity (Days)"
                                        type="number"
                                        placeholder="How many days is this benefit valid for?"
                                    />
                                </Box>
                            </Grid>

                            <Grid size={{ xs: 12, sm: 12 }}>
                                <CommonInput
                                    formik={formik}
                                    fieldPath={`loyaltySettings.loyaltyBenefits[${index}].displaySettings.displayTerms`}
                                    label="Terms"
                                    placeholder="Please enter the terms of the voucher."
                                    multiline={true}
                                />
                            </Grid>
                        </Grid>
                    </Paper>
                );
            })}
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    py: 2
                }}
            >
                <Button sx={{ width: '100%' }} onClick={handleAddBenefitOnClick} variant="contained">
                    Add Reward
                </Button>
            </Box>
        </>
    );
};
